/**
 * One parser for every shape a recipe time arrives in.
 *
 * Recipe times reach us as three different things and every writer used to
 * re-derive its own regex for them:
 *   - ISO-8601 from schema.org JSON-LD        "PT7H10M", "PT0H55M", "P1DT2H"
 *   - French/English prose from DOM scrapes   "1 h 30", "20 min", "2 hours"
 *   - free text from Gemini extraction        "15 minutes"
 *
 * The copy in `scripts/processRecipeKeeper.js` matched `/PT(\d+)M/`, which reads
 * `PT7H` as 0 and `PT1H30M` as 0. That is how "Gigot d'agneau de 7 heures" came
 * to ship as a 10-minute recipe and why no recipe in the bundled catalog was
 * longer than 80 minutes. Anything that turns a recipe time into a number must
 * come through here.
 *
 * Unrelated to `src/utils/parseDuration.ts`, which reads step prose ("simmer
 * for 10 minutes") into timer seconds.
 */

/** ISO-8601 duration, anchored so a stray "PT" inside prose cannot match. */
const ISO_DURATION = /^P(?:(\d+(?:[.,]\d+)?)D)?(?:T(?:(\d+(?:[.,]\d+)?)H)?(?:(\d+(?:[.,]\d+)?)M)?(?:(\d+(?:[.,]\d+)?)S)?)?$/i;

/** "2 h", "2 hrs", "2 hours", "2 heures" — `(?![a-z])` so "1h30" still matches. */
const PROSE_HOURS = /(\d+(?:[.,]\d+)?)\s*(?:heures?|hours?|hrs?|h)(?![a-z])/i;

/** "30 min", "30 mins", "30 minutes", "30 m". */
const PROSE_MINUTES = /(\d+(?:[.,]\d+)?)\s*(?:minutes?|mins?|m)(?![a-z])/i;

function toNumber(raw: string): number {
  const n = parseFloat(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Minutes, rounded, or 0 when the value carries no time.
 *
 * 0 means "unknown" throughout the app — never "instant" — so callers should
 * omit the field rather than persist a default when this returns 0.
 */
export function parseRecipeDuration(value: string | number | null | undefined): number {
  if (value == null) return 0;

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
  }

  const text = value.trim();
  if (!text) return 0;

  // Some feeds emit "PT1H 30M"; collapse before the anchored match.
  const iso = text.replace(/\s+/g, '').match(ISO_DURATION);
  if (iso) {
    const minutes =
      toNumber(iso[1] || '0') * 1440 +
      toNumber(iso[2] || '0') * 60 +
      toNumber(iso[3] || '0') +
      toNumber(iso[4] || '0') / 60;
    return Math.round(minutes);
  }

  const hours = text.match(PROSE_HOURS);
  const mins = text.match(PROSE_MINUTES);

  if (!hours && !mins) {
    // A bare number is minutes — "45", never "45 hours".
    const bare = text.match(/^(\d+(?:[.,]\d+)?)$/);
    return bare ? Math.round(toNumber(bare[1])) : 0;
  }

  let minutes = hours ? toNumber(hours[1]) * 60 : 0;
  if (mins) {
    minutes += toNumber(mins[1]);
  } else if (hours) {
    // "1 h 30" — a bare number trailing the hour marker is minutes.
    const trailing = text.slice(hours.index! + hours[0].length).match(/^\s*(\d{1,2})(?![\d.,])/);
    if (trailing) minutes += toNumber(trailing[1]);
  }

  return Math.round(minutes);
}

/**
 * The precedence every writer shares: the publisher's own total wins, and
 * prep + cook is the fallback when it did not publish one. Returns 0 when the
 * source gave nothing — persist nothing in that case.
 */
export function resolveReadyInMinutes(input: {
  totalTime?: string | number | null;
  prepTime?: string | number | null;
  cookTime?: string | number | null;
}): { readyInMinutes: number; preparationMinutes: number; cookingMinutes: number } {
  const preparationMinutes = parseRecipeDuration(input.prepTime);
  const cookingMinutes = parseRecipeDuration(input.cookTime);
  const total = parseRecipeDuration(input.totalTime);

  return {
    readyInMinutes: total > 0 ? total : preparationMinutes + cookingMinutes,
    preparationMinutes,
    cookingMinutes,
  };
}
